import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Mail, MapPin, Phone, Send } from 'lucide-react';
import { useIsKenya, getPhoneDisplay } from '../hooks/useIsKenya';
export function ContactPage() {
  const isKenya = useIsKenya();
  const phoneDisplay = getPhoneDisplay(isKenya);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch('https://formspree.io/f/mreayoye', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message
        })
      });
      if (response.ok) {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          message: ''
        });
      }
    } catch (err) {
      // handle error silently
    } finally {
      setIsSubmitting(false);
    }
  };
  return <main className="pt-24" data-id="element-1261">
      {/* Hero */}
      <Section background="default" data-id="element-1262">
        <div className="max-w-4xl" data-id="element-1263">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-5xl lg:text-6xl font-bold font-heading text-[#0C0C0C] mb-6 leading-tight" data-id="element-1264">
            Let's Talk About Your Project
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-gray-600 leading-relaxed" data-id="element-1265">
            Tell us about your goals, challenges, or ideas - we'll help you find
            the right digital solution.
          </motion.p>
        </div>
      </Section>

      {/* Contact Form & Info */}
      <Section background="white" data-id="element-1266">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-1267">
          {/* Form */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} data-id="element-1268">
            {submitted ? <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex flex-col items-start justify-center h-full py-16" data-id="element-1269">
                <div className="w-14 h-14 bg-[#00316B]/10 rounded-full flex items-center justify-center mb-6" data-id="element-1270">
                  <Send className="w-6 h-6 text-[#00316B]" data-id="element-1271" />
                </div>
                <h3 className="text-2xl font-bold font-heading text-[#0C0C0C] mb-3" data-id="element-1272">
                  Message Sent!
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6" data-id="element-1273">
                  Thank you for reaching out. We've received your message and
                  will get back to you within 2–4 business hours.
                </p>
                <button onClick={() => setSubmitted(false)} className="text-sm text-[#00316B] font-medium underline underline-offset-4 hover:text-[#FF6600] transition-colors" data-id="element-1274">
                  Send another message
                </button>
              </motion.div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1275">
                <div data-id="element-1276">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1277">
                    Your Name *
                  </label>
                  <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00316B]/20 focus:border-[#00316B] transition-colors" placeholder="Your Cute Name Here" data-id="element-1278" />
                </div>

                <div data-id="element-1279">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1280">
                    Email Address *
                  </label>
                  <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00316B]/20 focus:border-[#00316B] transition-colors" placeholder="Your Super Email Here" data-id="element-1281" />
                </div>

                <div data-id="element-1282">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1283">
                    Tell us about your project *
                  </label>
                  <textarea id="message" name="message" required rows={6} value={formData.message} onChange={handleChange} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00316B]/20 focus:border-[#00316B] transition-colors resize-none" placeholder="Describe your project, goals, timeline, and any other relevant details..." data-id="element-1284" />
                </div>

                <Button type="submit" size="lg" className="w-full" isLoading={isSubmitting} rightIcon={<Send className="w-4 h-4" data-id="element-1286" />} data-id="element-1285">
                  Send Message
                </Button>
              </form>}
          </motion.div>

          {/* Contact Info */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="lg:pl-8" data-id="element-1287">
            <h2 className="text-2xl font-bold font-heading text-[#0C0C0C] mb-8" data-id="element-1288">
              Get in Touch
            </h2>

            <div className="space-y-8 mb-12" data-id="element-1289">
              <div className="flex items-start" data-id="element-1290">
                <div className="w-12 h-12 bg-[#F8F8F6] rounded-lg flex items-center justify-center mr-4 shrink-0" data-id="element-1291">
                  <Mail className="w-5 h-5 text-[#00316B]" data-id="element-1292" />
                </div>
                <div data-id="element-1293">
                  <h3 className="font-semibold text-[#0C0C0C] mb-1" data-id="element-1294">Email</h3>
                  <a href="mailto:biz@belo.co.ke" className="text-gray-600 hover:text-[#00316B] transition-colors" data-id="element-1295">
                    biz@belo.co.ke
                  </a>
                </div>
              </div>

              <div className="flex items-start" data-id="element-1296">
                <div className="w-12 h-12 bg-[#F8F8F6] rounded-lg flex items-center justify-center mr-4 shrink-0" data-id="element-1297">
                  <Phone className="w-5 h-5 text-[#00316B]" data-id="element-1298" />
                </div>
                <div data-id="element-1299">
                  <h3 className="font-semibold text-[#0C0C0C] mb-1" data-id="element-1300">Phone</h3>
                  <a href="tel:+254701660306" className="text-gray-600 hover:text-[#00316B] transition-colors" data-id="element-1301">
                    {phoneDisplay}
                  </a>
                </div>
              </div>

              <div className="flex items-start" data-id="element-1302">
                <div className="w-12 h-12 bg-[#F8F8F6] rounded-lg flex items-center justify-center mr-4 shrink-0" data-id="element-1303">
                  <MapPin className="w-5 h-5 text-[#00316B]" data-id="element-1304" />
                </div>
                <div data-id="element-1305">
                  <h3 className="font-semibold text-[#0C0C0C] mb-1" data-id="element-1306">
                    Location
                  </h3>
                  <p className="text-gray-600" data-id="element-1307">Nairobi, Kenya</p>
                </div>
              </div>
            </div>

            <div className="p-6 bg-[#F8F8F6] rounded-xl" data-id="element-1308">
              <h3 className="font-bold font-heading text-[#0C0C0C] mb-2" data-id="element-1309">
                Response Time
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-1310">
                We typically respond within 2-4 hours during business days. For
                urgent inquiries, please give us a call or send a message on
                WhatsApp.
              </p>
            </div>
          </motion.div>
        </div>
      </Section>
    </main>;
}